import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import ShareButton from "@/components/ShareButton";
import { Leaf, SpinnerGap, MapPin, ArrowLeft } from "@phosphor-icons/react";

export default function SpeciesPage() {
  const { name } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/public/species/${encodeURIComponent(name)}`).then((r) => setData(r.data)).catch(() => setData({ photos: [] }));
  }, [name]);

  return (
    <PublicLayout>
      <section className="bg-gradient-to-b from-emerald-950 to-emerald-800 text-white">
        <div className="mx-auto max-w-6xl px-5 py-14">
          <Link to="/nature-pulse" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-200 hover:text-white">
            <ArrowLeft size={15} weight="bold" /> NaturePulse
          </Link>
          <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold capitalize backdrop-blur">
            <Leaf size={14} weight="fill" /> {data?.type || "Species"}
          </span>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tight sm:text-5xl">{decodeURIComponent(name)}</h1>
          {data?.scientific_name && <p className="mt-1 text-lg italic text-emerald-100/90">{data.scientific_name}</p>}
          {data && <p className="mt-4 text-sm text-emerald-200/80">{data.total} photo{data.total === 1 ? "" : "s"} across the network</p>}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-10">
        {data === null ? (
          <div className="grid place-items-center py-20"><SpinnerGap size={30} className="animate-spin text-emerald-600" weight="bold" /></div>
        ) : data.photos.length === 0 ? (
          <div className="grid place-items-center py-20 text-center text-muted-foreground">
            <Leaf size={40} weight="light" className="text-emerald-300" />
            <p className="mt-3 font-display text-lg font-bold text-zinc-700">No photos of this species yet</p>
            <Link to="/nature-pulse" className="mt-2 text-sm font-semibold text-emerald-700 hover:underline">Back to NaturePulse</Link>
          </div>
        ) : (
          <div className="columns-2 gap-4 sm:columns-3 lg:columns-4 [&>*]:mb-4" data-testid="species-grid">
            {data.photos.map((p) => (
              <Link key={p.id} to={`/p/${p.id}`} data-testid={`species-photo-${p.id}`}
                className="group relative block break-inside-avoid overflow-hidden rounded-lg bg-zinc-100 shadow-sm ring-1 ring-black/5">
                <img src={assetUrl(p.image_url)} alt={p.title || "photo"} loading="lazy"
                  className="w-full transition-transform duration-500 group-hover:scale-[1.04]" />
                <div className="absolute right-2 top-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                  <ShareButton id={p.id} title={p.title} />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <p className="line-clamp-1 text-sm font-bold text-white">{p.title || "Untitled"}</p>
                  {p.location?.city && (
                    <p className="flex items-center gap-1 text-xs text-white/80">
                      <MapPin size={11} weight="fill" />{p.location.city}{p.location.country ? `, ${p.location.country}` : ""}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
